'use client';

import { useMemo, useState } from 'react';
import styles from './blog.module.css';
import type { BlogCategoryInfo, BlogLocale, BlogPostSummary } from './blog-data';

const COPY = {
  pt: {
    searchLabel: 'Buscar no blog',
    searchPlaceholder: 'Ex.: Lua, casas, relacionamento…',
    all: 'Todos os artigos',
    updated: 'atualizado',
    results: 'artigos encontrados',
    noResults: 'Nenhum artigo encontrado para essa busca.',
  },
  en: {
    searchLabel: 'Search the blog',
    searchPlaceholder: 'E.g. Moon, houses, relationships…',
    all: 'All articles',
    updated: 'updated',
    results: 'articles found',
    noResults: 'No articles match this search.',
  },
  es: {
    searchLabel: 'Buscar en el blog',
    searchPlaceholder: 'Ej.: Luna, casas, relaciones…',
    all: 'Todos los artículos',
    updated: 'actualizado',
    results: 'artículos encontrados',
    noResults: 'No encontramos artículos para esta búsqueda.',
  },
} as const;

type Props = {
  locale: string;
  posts: BlogPostSummary[];
  categories: BlogCategoryInfo[];
  activeCategory?: string;
};

function localeCopy(locale: string) {
  return COPY[(locale === 'pt' || locale === 'es' ? locale : 'en') as BlogLocale];
}

export function BlogIndexControls({ locale, posts, categories, activeCategory }: Props) {
  const copy = localeCopy(locale);
  const [query, setQuery] = useState('');
  const filteredPosts = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase(locale);
    if (!normalized) {
      return posts;
    }
    return posts.filter((post) =>
      [post.title, post.description, ...post.keywords].some((value) =>
        value.toLocaleLowerCase(locale).includes(normalized)
      )
    );
  }, [locale, posts, query]);

  return (
    <>
      <div className={styles.discovery}>
        <label className={styles.searchLabel} htmlFor="blog-search">
          {copy.searchLabel}
        </label>
        <input
          className={styles.searchInput}
          id="blog-search"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={copy.searchPlaceholder}
          type="search"
          value={query}
        />
      </div>
      <nav aria-label={copy.all} className={styles.categoryNav}>
        <a
          className={`${styles.categoryLink} ${!activeCategory ? styles.categoryLinkActive : ''}`}
          href={`/${locale}/blog`}
          aria-current={!activeCategory ? 'page' : undefined}
        >
          {copy.all}
        </a>
        {categories.map((category) => (
          <a
            aria-current={activeCategory === category.key ? 'page' : undefined}
            className={`${styles.categoryLink} ${activeCategory === category.key ? styles.categoryLinkActive : ''}`}
            href={`/${locale}/blog/category/${category.slug}`}
            key={category.key}
          >
            {category.label}
            <span>{category.count}</span>
          </a>
        ))}
      </nav>
      <p aria-live="polite" className={styles.resultCount}>
        {filteredPosts.length} {copy.results}
      </p>
      <div className={styles.indexList}>
        {filteredPosts.length === 0 && <p>{copy.noResults}</p>}
        {filteredPosts.map((post) => (
          <a className={styles.indexCard} href={`/${locale}/blog/${post.slug}`} key={post.slug}>
            <small>
              {post.category.label} · {copy.updated} {post.updated}
            </small>
            <h2>{post.title}</h2>
            <p>{post.description}</p>
          </a>
        ))}
      </div>
    </>
  );
}
